/**
 * terrain_kernel — host side of the AssemblyScript terrain field kernel
 * (client/assembly/terrain_kernel.ts → public/wasm/terrain_kernel.wasm).
 *
 * The kernel is an ACCELERATOR behind shared/terrain.ts: `installTerrainKernel`
 * hands the JS `terrainHeight` / `biomeWeights` a hook that runs the same math
 * in Wasm. Parity is bit-exact by construction (IEEE-exact ops; exp/sin/cos/
 * hypot imported from THIS realm's Math; FNV seed prefixes computed here with
 * the same code shared/terrain.ts uses) and proven by verify:terrain-kernel.
 * Anything that fails — no Wasm, fetch error, a bad module — leaves the JS
 * path in charge. Opt out at runtime with localStorage `atlas:terrain-kernel`
 * = 'off' (or `?terrainKernel=off`).
 */
import { setTerrainKernel, terrainKernelActive, type TerrainKernelHook } from '../../../shared/terrain.js';
import { CITY_PIN_RADIUS, CITY_PIN_FALLOFF, BIOME_WAVELENGTH } from '../../../shared/constants.js';

/** Derived-seed suffixes in SLOT ORDER — mirror of the kernel's S_* constants. */
export const SEED_SUFFIXES: readonly string[] = [
  '', 'temp', 'moist', 'rug', 'o', 'rk', 'm', 'dang', 'dwarp', 'd', 'f',
  'cont0', 'cont1', 'cont2', 'wx', 'wx2', 'wz', 'wz2',
  'ridge0', 'ridge1', 'ridge2', 'ridge3', 'rseg', 'mmask',
  'er0', 'er1', 'er2', 'er3', 'er4',
  'rk:er0', 'rk:er1', 'rk:er2', 'rk:er3',
];
const SEED_SLOTS = 40;

interface KernelExports {
  memory: WebAssembly.Memory;
  configure(pinR: number, pinF: number, biomeWl: number): void;
  seedTablePtr(): number;
  outPtr(): number;
  heightRaw(x: number, z: number): number;
  height(x: number, z: number): number;
  biomeWeights(x: number, z: number): void;
  fillChunk(worldCX: number, worldCZ: number, size: number, seg: number): number;
  chunkHeightsPtr(): number;
  chunkWeightsPtr(): number;
  cacheStats(): number;
  cacheMissCount(): number;
  resetCache(): void;
}

/** A batched chunk field: views into kernel memory, valid until the next fillChunk(). */
export interface ChunkField {
  count: number;
  /** f64 per vertex (with the talus relaxation) — the same values `terrainHeight` returns. */
  heights: Float64Array;
  /** 6 f64 per vertex in BIOMES order — the same values `biomeWeights` returns. */
  weights: Float64Array;
}

/** The FNV-1a prefix shared/terrain.ts caches per seed string (`seed` + ':'), exact. */
export function seedPrefixHash(seedString: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < seedString.length; i++) h = Math.imul(h ^ seedString.charCodeAt(i), 16777619) >>> 0;
  h = Math.imul(h ^ 58, 16777619) >>> 0; // ':'
  return h;
}

/** The kernel's seed table for a base seed: one prefix per derived seed slot. */
export function seedTableFor(seed: string): Uint32Array {
  const t = new Uint32Array(SEED_SLOTS);
  for (let i = 0; i < SEED_SUFFIXES.length; i++) {
    const suffix = SEED_SUFFIXES[i];
    t[i] = seedPrefixHash(suffix === '' ? seed : `${seed}:${suffix}`);
  }
  return t;
}

export const KERNEL_IMPORTS = {
  env: {
    jsExp: Math.exp, jsSin: Math.sin, jsCos: Math.cos, jsHypot: Math.hypot,
    abort: () => { throw new Error('terrain kernel abort'); },
  },
};

export class TerrainKernel implements TerrainKernelHook {
  private readonly tables = new Map<string, Uint32Array>();
  private currentSeed = '';
  private readonly seedView: Uint32Array;
  private readonly outView: Float64Array;
  constructor(readonly exports: KernelExports) {
    exports.configure(CITY_PIN_RADIUS, CITY_PIN_FALLOFF, BIOME_WAVELENGTH);
    this.seedView = new Uint32Array(exports.memory.buffer, exports.seedTablePtr(), SEED_SLOTS);
    this.outView = new Float64Array(exports.memory.buffer, exports.outPtr(), 6);
  }
  private select(seed: string): void {
    if (seed === this.currentSeed) return;
    let t = this.tables.get(seed);
    if (!t) { t = seedTableFor(seed); this.tables.set(seed, t); }
    this.seedView.set(t);
    this.currentSeed = seed;
  }
  height(seed: string, x: number, z: number): number {
    this.select(seed);
    return this.exports.height(x, z);
  }
  heightRaw(seed: string, x: number, z: number): number {
    this.select(seed);
    return this.exports.heightRaw(x, z);
  }
  biomeWeights(seed: string, x: number, z: number, out: Float64Array): void {
    this.select(seed);
    this.exports.biomeWeights(x, z);
    out[0] = this.outView[0]; out[1] = this.outView[1]; out[2] = this.outView[2];
    out[3] = this.outView[3]; out[4] = this.outView[4]; out[5] = this.outView[5];
  }
  /**
   * The whole chunk field in one call (Phase 1b). Returns null when the chunk
   * is larger than the kernel's static buffers (seg > 65) — callers fall back
   * to the per-vertex path. The views alias kernel memory: consume before the
   * next fillChunk / any memory growth.
   */
  fillChunk(seed: string, worldCX: number, worldCZ: number, size: number, seg: number): ChunkField | null {
    this.select(seed);
    const count = this.exports.fillChunk(worldCX, worldCZ, size, seg);
    if (count <= 0) return null;
    const buf = this.exports.memory.buffer;
    return {
      count,
      heights: new Float64Array(buf, this.exports.chunkHeightsPtr(), count),
      weights: new Float64Array(buf, this.exports.chunkWeightsPtr(), count * 6),
    };
  }
  /** Lattice-corner cache counters (diagnostics). */
  cacheStats(): { hits: number; misses: number } {
    return { hits: this.exports.cacheStats() >>> 0, misses: this.exports.cacheMissCount() >>> 0 };
  }
}

/** Instantiate from raw bytes (Node harnesses) or a Response (browser streaming). */
export async function loadTerrainKernel(source: BufferSource | Response | Promise<Response>): Promise<TerrainKernel> {
  let instance: WebAssembly.Instance;
  if (source instanceof ArrayBuffer || ArrayBuffer.isView(source)) {
    instance = (await WebAssembly.instantiate(source as BufferSource, KERNEL_IMPORTS)).instance;
  } else if (typeof WebAssembly.instantiateStreaming === 'function') {
    instance = (await WebAssembly.instantiateStreaming(source as Response | Promise<Response>, KERNEL_IMPORTS)).instance;
  } else {
    const bytes = await (await source).arrayBuffer();
    instance = (await WebAssembly.instantiate(bytes, KERNEL_IMPORTS)).instance;
  }
  return new TerrainKernel(instance.exports as unknown as KernelExports);
}

/** Public URL of the compiled kernel (served from public/). */
export const TERRAIN_KERNEL_URL = '/wasm/terrain_kernel.wasm';

/** True when the wasm kernels are switched off for this session (localStorage `atlas:terrain-kernel` = 'off' or `?terrainKernel=off`). */
export function optedOut(): boolean {
  try {
    if (typeof localStorage !== 'undefined' && localStorage.getItem('atlas:terrain-kernel') === 'off') return true;
    if (typeof location !== 'undefined' && /[?&]terrainKernel=off\b/.test(location.search)) return true;
  } catch { /* workers: no localStorage/location access issues → default on */ }
  return false;
}

let installed: TerrainKernel | null = null;
/** The kernel this realm installed (null on the JS path). */
export function currentTerrainKernel(): TerrainKernel | null { return installed; }

let installing: Promise<boolean> | null = null;
/**
 * Fetch + install the kernel for this realm (main thread or a worker). Resolves
 * true when the native path is serving. Idempotent; never throws.
 */
export function installTerrainKernelFromUrl(url: string = TERRAIN_KERNEL_URL, log: (m: string) => void = () => {}): Promise<boolean> {
  if (terrainKernelActive()) return Promise.resolve(true);
  if (installing) return installing;
  installing = (async () => {
    try {
      if (optedOut() || typeof WebAssembly === 'undefined' || typeof fetch !== 'function') return false;
      const t0 = Date.now();
      const kernel = await loadTerrainKernel(fetch(url));
      installed = kernel;
      setTerrainKernel(kernel);
      log(`native terrain kernel ready in ${Date.now() - t0}ms`);
      return true;
    } catch (err) {
      log(`native terrain kernel unavailable (${err}) — JS path`);
      return false;
    }
  })();
  return installing;
}
